import { useCallback, useMemo, useState } from "react"
import { TablePaginationConfig } from "antd/lib/table"
import { defaultQueryParams } from "Shared/queries/vendors/consts"
import { QueryParamsFilter } from "@finway-group/shared/lib/models/queryParams/queryParams.types"
import { DELIVERY_NOTE_QUERY_KEYS, useDeleteDeliveryNoteMutation, useDeliveryNotesQuery, defaultSortParams } from "Features/pages/deliveryNotes/queries/deliveryNote.queries"
import { useTranslation } from "react-i18next"
import { queryClient } from "Shared/config/reactQueryConfig"
import { GENERAL_QUERY_STALE_TIME, QueryParams } from "Shared/queries/consts"
import { NotificationService } from "Shared/services"
import { DeliveryNote } from "Features/pages/deliveryNotes/types/deliveryNote.interfaces"
import { DeliveryNoteApiService } from "Features/pages/deliveryNotes/service/deliveryNoteApi.service"
import { deliveryNoteTransformer } from "Features/pages/deliveryNotes/types/utils/helper.util"
import useLocalStorage from "Shared/hooks/useLocalStorage"
import { DELIVERY_NOTES_QUERY_PARAMS, DELIVERY_NOTES_TABLE_LIMIT } from "Shared/services/localstorage.service"
import { useSetQueryParamsStorage } from "./usePaginationStorage.hook"

export const useDeliveryNote = () => {
    const { t } = useTranslation()
    const getDeliveryNote = async (dnId: string) => {
        try {
            const result = await queryClient.fetchQuery<DeliveryNote>({
                queryKey: DELIVERY_NOTE_QUERY_KEYS.DETAIL(dnId),
                queryFn: () => DeliveryNoteApiService.getDeliveryNoteById(dnId),
                staleTime: GENERAL_QUERY_STALE_TIME,
            })

            return result
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t(`error:error`))
            return undefined
        }
    }

    return {
        getDeliveryNote,
    }
}

export const useDeliveryNotes = (filter: QueryParamsFilter = {}) => {
    const [search, setSearch] = useState("")
    const [currentPage, setCurrentPage] = useState(defaultQueryParams.page)
    const [limit, setLimit] = useLocalStorage(DELIVERY_NOTES_TABLE_LIMIT, defaultQueryParams.limit)
    const [sortParams, setSortParams] = useState<QueryParams["sortParams"]>(defaultQueryParams.sortParams)
    useSetQueryParamsStorage({ page: currentPage, limit, search, sortParams }, DELIVERY_NOTES_QUERY_PARAMS)

    const { data: serverDNs, isLoading } = useDeliveryNotesQuery({ page: currentPage, limit, search, filter, sortParams })

    const tableData = useMemo(() => (serverDNs ? serverDNs.docs.map(deliveryNoteTransformer) : []), [serverDNs])

    const handleSearch = useCallback(
        (value: string) => {
            setSearch(value)
            setCurrentPage(defaultQueryParams.page)
        },
        [setSearch],
    )

    const isFilterApplied = Object.keys(filter).length > 0
    const isNoSearchDataFound = tableData?.length === 0 && (!!search || isFilterApplied)
    const isTableEmpty = tableData?.length === 0 && !search && !isFilterApplied

    const pagination: Pick<Required<TablePaginationConfig>, "total" | "current" | "pageSize"> = {
        total: serverDNs?.totalDocs || 0,
        current: currentPage,
        pageSize: limit,
    }

    return {
        tableData,
        isLoading,
        handleSearch,
        search,
        isNoSearchDataFound,
        isTableEmpty,
        pagination,
        setCurrentPage,
        setSortParams,
        resetSorting: () => {
            setSortParams(defaultSortParams)
        },
        setPageSize: setLimit,
    }
}

export const useMutateDeliveryNotes = () => {
    const deleteDeliveryNoteMutation = useDeleteDeliveryNoteMutation()

    const deleteDeliveryNote = useCallback(
        (key: string) => {
            deleteDeliveryNoteMutation.mutate(key)
        },
        [deleteDeliveryNoteMutation],
    )

    return {
        deleteDeliveryNote,
    }
}
